import React, { useEffect, useRef, useState } from "react";
import { InventoryApiService } from "../../Service/InventoryApiService";
import { DataGrid } from "@mui/x-data-grid";
import {
  Typography,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { GridColDef } from "@mui/x-data-grid";
import "../Dynamic/UserList.scss";
import { CommonService } from "../../Service/CommonService";
import { useHistory } from "react-router";
import StatusDropdown from "../Common/StatusDropdown";
import ExpandMore from "../../assets/images/icons/ExpandMore.svg";
import { Form } from "react-bootstrap";
import DataGridLoader from "../Common/DataGridLoader";

const CompanyContractCapacityListing: React.FC = () => {
  const history = useHistory();
  const [contractCapacity, setContractCapacity] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const buttonRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const divRef = useRef<HTMLDivElement>(null);
  const [sortField, setSortField] = useState("createdDate"); // Default sort
  const [sortDirection, setSortDirection] = useState("desc"); // Default ascending
  const [filter, setFilter] = useState<any>({
    companyName: null,
    numberOfContract: null,
    totalCapacity: null,
    utilizedCapacity: null,
    availableCapacity: null,
    status: null,
  });
  const [showDropdown, setShowDropdown] = useState(false);
  const statusOptions = [
    { value: "NORMAL_USAGE", label: "Normal Usage"},
    { value: "NEEDS_UPSIZING", label: "Needs Upsizing"},
    { value: "NEEDS_DOWNSIZING", label: "Needs Downsizing"},
  ];

  //formate the status
  function formatStatus(status: string): string {
    const formattedStatus = status
      .split(/[_\\]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

    return formattedStatus;
  }
  const handleSortChange = (sortModel) => {
    if (sortModel.length > 0) {
      const newSortField = sortModel[0].field;
      const newSortDirection = sortModel[0].sort; // "asc" or "desc" directly from DataGrid

      setSortField(newSortField);
      setSortDirection(newSortDirection);

      fetchAllCompaniesContractCapacity(
        page,
        pageSize,
        newSortField,
        newSortDirection,
        filter
      );
    }
  };

  const fetchAllCompaniesContractCapacity = async (
    page: any,
    size: any,
    sortField: any,
    sortDirection: any,
    filter: any
  ) => {
    const controller = new AbortController();
    const signal = controller.signal;
    try {
      setLoading(true);
      // Cancel the previous request if any
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      const response = await InventoryApiService.fetchAllCompaniesContractCapacity(
        page,
        size,
        sortField,
        sortDirection,
        filter,
        CommonService.isAdmin() ? null : CommonService.getCompanyId(),
        signal
      );
      setContractCapacity(response.data.content);
      setTotalCount(response.data.totalElements);
    } catch (error) {
      console.log("error occurred while api calling " + error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllCompaniesContractCapacity(
      page,
      pageSize,
      sortField,
      sortDirection,
      filter
    );
  }, []);

  const handleStatusFilterChange = (
    event: any,
    value: string,
    status: string | null
  ) => {
    const isChecked = event.target.checked;

    // Convert the status string to an array
    const statusArray = status ? status.split(",") : [];

    // Update the status list based on checkbox state
    const updatedStatusArray = isChecked
      ? [...statusArray, value] // Add status if checked
      : statusArray.filter((s) => s !== value); // Remove status if unchecked

    // Convert array back to a comma-separated string
    const updatedStatus =
      updatedStatusArray?.length > 0 ? updatedStatusArray.join(",") : null;

    // Update the filter state with the modified status string
    setFilter({ ...filter, status: updatedStatus });

    //call api
    fetchAllCompaniesContractCapacity(page, pageSize, sortField, sortDirection, {
      ...filter,
      status: updatedStatus,
    });
  };

  const getStatusOptions = () => {
    return statusOptions;
  };

  const handleRowClick = (params: any) => {
    const linkPrefix = CommonService.isAdmin() ? "/master" : "/user";
    history.push(`${linkPrefix}/view-contract/${params.row.id}`);
  };

  //add tooltip
  const CellWithTooltip = ({ value }: { value: string | number }) => {
    return (
      <Tooltip arrow title={value?.toString()}>
        <span>{value?.toString()}</span>
      </Tooltip>
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        divRef.current &&
        !divRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]); // Only run when dropdown is toggled

  const handleFilterChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: keyof any
  ) => {
    const value = event.target.value;

    // Prepare a temporary filter object with the updated field value
    const tempFilter = {
      ...filter, // Spread existing filter state
      [field]: field === "status" && value === "" ? null : value, // Update field only if the condition is met
    };

    // Update the filter state
    setFilter(tempFilter);

    //call api
    fetchAllCompaniesContractCapacity(
      page,
      pageSize,
      sortField,
      sortDirection,
      tempFilter
    );
  };

  const toggleDropdown = () => {
    setShowDropdown((prev) => !prev);
  };

  const columns: GridColDef[] = [
    {
      field: "companyName",
      headerName: "Company",
      flex: 1,
      minWidth: 150,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "140px" }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.companyName}
              defaultValue={filter.companyName}
              onChange={(event) => handleFilterChange(event, "companyName")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },
    {
      field: "numberOfContract",
      headerName: "Number of Contract",
      flex: 1,
      minWidth: 150,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "140px" }}>
            <input
              type="number"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.numberOfContract}
              defaultValue={filter.numberOfContract}
              onChange={(event) =>
                handleFilterChange(event, "numberOfContract")
              }
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },
    {
      field: "totalCapacity",
      headerName: "Total Capacity",
      minWidth: 150,
      flex: 1,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "140px" }}>
            <input
              type="number"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.totalCapacity}
              defaultValue={filter.totalCapacity}
              onChange={(event) => handleFilterChange(event, "totalCapacity")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => (
        <CellWithTooltip value={params.value || "-"} />
      ),
    },
    {
      field: "utilizedCapacity",
      headerName: "Utilized Capacity",
      minWidth: 150,
      flex: 1,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "140px" }}>
            <input
              type="number"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              value={filter.utilizedCapacity}
              defaultValue={filter.utilizedCapacity}
              onChange={(event) =>
                handleFilterChange(event, "utilizedCapacity")
              }
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => {
        const value = params.value || "-";
        const percentage = params.row.utilizedCapacityPercentage !== undefined
          ? ` (${params.row.utilizedCapacityPercentage}%)`
          : "";

        return <CellWithTooltip value={`${value} | ${percentage}`} />;
      },
    },
    {
      field: "availableCapacity",
      headerName: "Available Capacity",
      minWidth: 150,
      flex: 1,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: "140px" }}>
            <input
              type="number"
              className="form-control border mt-1"
              style={{
                MozAppearance: 'textfield', // Firefox
                WebkitAppearance: 'none',  // Chrome/Safari
                margin: 0,
                textIndent: '24px',
                boxShadow: 'none',
              }}
              placeholder="Search"
              value={filter.availableCapacity}
              defaultValue={filter.availableCapacity}
              onChange={(event) =>
                handleFilterChange(event, "availableCapacity")
              }
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => {
        let value = params.value ?? "-";
        let percentageValue = params.row.availableCapacityPercentage;

        // Check for negative or undefined values
        if (percentageValue === undefined || percentageValue < 0) {
          percentageValue = 0;
          value = 0;
        }

        const percentage = `(${percentageValue}%)`;

        return <CellWithTooltip value={`${value} | ${percentage}`} />;
      }
    },
    {
      field: "status",
      headerName: "Status",
      cellClassName: "status-badge",
      headerClassName: "table-header border-0 my-2 h-100",
      sortable: false, // disables sorting for this column
      flex: 1,
      minWidth: 180,
      renderCell: (params: any) => (
        <div
          className="dropdown-nospacing"
          onClick={(e) => e.stopPropagation()}
        >
          <StatusDropdown
            currentStatus={formatStatus(params.row.status)}
            onStatusChange={(e) => console.log(e)}
            disableCondition={true}
            allowedLabels={[]}
          />
        </div>
      ),
      renderHeader: (params) => (
        <>
          <div>
            <Typography
              variant="body2"
              sx={{ color: "#6F6F6F", fontSize: "14px" }}
            >
              {params.colDef.headerName}
            </Typography>

            <div className="position-relative" style={{ width: "170px" }}>
              <div
                className="form-control mb-1"
                style={{
                  cursor: "pointer",
                  borderBottomColor: "#D1D1D1",
                  marginTop: "4px",
                  background: "#fff",
                  padding: "5px",
                  overflowX: "hidden",
                  paddingRight: "16px",
                  textOverflow: "ellipsis",
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleDropdown();
                }}
                ref={buttonRef}
              >
                {filter.status?.length > 0
                  ? CommonService.formatStatus(filter.status).join(", ")
                  : "Select Status"}

                <img
                  src={ExpandMore}
                  className="position-absolute top-50 end-0 translate-middle"
                />
              </div>
              {/* Custom Dropdown */}
              {showDropdown && (
                <div
                  className="dropdown-menu show status-multiselect-dropdown"
                  onClick={(event) => event.stopPropagation()}
                  ref={divRef}
                >
                  {getStatusOptions().map((option) => (
                    <Form.Check
                      key={option.value}
                      id={`status-${option.value}`}
                      type="checkbox"
                      label={option.label}
                      value={option.value}
                      checked={
                        filter.status?.split(",").includes(option.value) ||
                        false
                      }
                      onChange={(event) => {
                        event.stopPropagation();
                        handleStatusFilterChange(
                          event,
                          option.value,
                          filter.status
                        );
                      }}
                      className="cursor-pointer"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      ),
    },
  ];

  const onPaginationModelChange = (newPaginationModel) => {
    setPage(newPaginationModel.page);
    setPageSize(newPaginationModel.pageSize);

    fetchAllCompaniesContractCapacity(
      newPaginationModel.page,
      newPaginationModel.pageSize,
      sortField,
      sortDirection,
      filter
    );
  };

  return (
      <div className="user-table-container">
        <div className="userTable">
          <DataGrid
            rows={contractCapacity}
            columns={columns}
            className="data-grid support-req--listing"
            initialState={{
              pagination: {
                paginationModel: { page, pageSize },
              },
            }}
            pagination
            rowCount={totalCount}
            getRowId={(row) => row.companyName} // use unique companyName as ID
            paginationModel={{ page, pageSize }}
            paginationMode="server"
            sortingMode="server"
            onSortModelChange={handleSortChange}
            onPaginationModelChange={onPaginationModelChange}
            disableRowSelectionOnClick={true}
            pageSizeOptions={[10, 20, 50, 100]}
            loading={loading}
            slots={{
              loadingOverlay: DataGridLoader,
            }}
            disableColumnResize
            // onRowClick={handleRowClick}
          />
        </div>
      </div>
  );
};

export default CompanyContractCapacityListing;
